import { useContext, useEffect, useState } from "react";
import referralArrow from '../imgs/referral_copy_arrow.png';
import plusSign from '../imgs/referral_plus_sign.png';
import copiedCheckmark from '../imgs/promo_copied_checkmark.png';
import { trackGoal } from "../helpers/tracker";
import styles from './ReferralPromocodeCard.module.css';
import { ReferralAppContext } from "./ReferralApp";
import CircularProgress from "@material-ui/core/CircularProgress";
import referralCreateCode from '../imgs/referral_create_code.png';
import { openPassport } from "../mainscreen/header/Avatar";
import { IReferralInfo } from "./ReferralInfo";
import { instance } from "../networking/axios";

export default function ReferralPromocodeCard() {
    const appContext = useContext(ReferralAppContext);
    const [referral, setReferral] = useState(appContext?.referral);

    const hasReferral = referral?.has_referral ?? false;
    const showCode = hasReferral && referral?.code != null;

    if (showCode) {
        return <ReferralPromocodeInfoCard
            moneyEarned={`${referral.total_bonuses}`}
            code={referral?.code ?? ""}
            isMobile={appContext?.isMobile ?? false}
            codeTemplate={appContext?.promocodeTemplate ?? ""}
        />
    } else {
        return <CreatePromocodeCard
            isMobile={appContext?.isMobile ?? false}
            onUpdateInfo={(newInfo) => setReferral(newInfo)}
        />;
    }
}

interface IReferralPromocodeInfoCardProps {
    code: string
    codeTemplate?: string,
    moneyEarned: string
    isMobile: boolean
}

function ReferralPromocodeInfoCard(props: IReferralPromocodeInfoCardProps) {
    const [showCodeCopied, setShowCodeCopied] = useState<boolean | null>(null);

    useEffect(() => {
        if (showCodeCopied) {
            setTimeout(() => {
                setShowCodeCopied(false);
            }, 1500);
        }
    }, [showCodeCopied]);

    function copyCode() {
        trackGoal("login_or_create_code_or_copy");
        if (props.codeTemplate) {
            copyTextToClipboard(props.codeTemplate.replaceAll("{promocode}", props.code));
        } else {
            copyTextToClipboard(props.code)
        }
        setShowCodeCopied(true);
    }

    if (props.isMobile) {
        return <div className={styles.card_mobile}>
            <span className={styles.title_label_mobile}>Ваш промокод</span>
            {
                showCodeCopied != null &&
                <div className={`${styles.code_copied_mobile} ${showCodeCopied ? styles.code_copied_fade_in : styles.code_copied_fade_out}`}>
                    <img src={copiedCheckmark} className={styles.copied_checkmark} />
                    <span className={styles.copied_text}>Промокод скопирован</span>
                </div>
            }
            <span className={styles.code_label_mobile}>{props.code}</span>
            <div className={styles.info_row_mobile}>
                <InfoMobile title="Заработано" subtitle={props.moneyEarned} subtitleIcon={plusSign} />
            </div>
            <div className={styles.separator_mobile} />
            <button className={styles.copy_button_mobile} onClick={() => copyCode()}>
                Скопировать
                <div style={{ flexGrow: 1 }} />
                <img className={styles.copy_arrow} src={referralArrow} />
            </button>
        </div>
    } else {
        return <div className={styles.card} onClick={() => copyCode()}>
            <span className={styles.title_label}>Ваш промокод</span>
            {
                showCodeCopied != null &&
                <div className={`${styles.code_copied} ${showCodeCopied ? styles.code_copied_fade_in : styles.code_copied_fade_out}`}>
                    <img src={copiedCheckmark} className={styles.copied_checkmark} />
                    <span className={styles.copied_text}>Промокод скопирован</span>
                </div>
            }
            <span className={styles.code_label}>{props.code}</span>
            <div className={styles.info_row}>
                <Info title="Заработано" subtitle={props.moneyEarned} subtitleIcon={plusSign} />
            </div>
            <div className={styles.separator} />
            <div className={styles.copy_button}>
                Скопировать
                <img className={styles.copy_arrow} src={referralArrow} />
            </div>
        </div>
    }
}

function Info({ title, subtitle, subtitleIcon }: { title: string, subtitle: string, subtitleIcon?: string }) {
    return <div className={styles.info_row_item}>
        <span className={styles.info_row_item_title}>{title}</span>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "baseline" }}>
            {subtitleIcon && <img className={styles.plus_sign} src={subtitleIcon} />}
            <span className={styles.info_row_item_subtitle}>{subtitle}</span>
        </div>
    </div>
}

function InfoMobile({ title, subtitle, subtitleIcon }: { title: string, subtitle: string, subtitleIcon?: string }) {
    return <div className={styles.info_row_item}>
        <span className={styles.info_row_item_title_mobile}>{title}</span>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "baseline" }}>
            {subtitleIcon && <img className={styles.plus_sign} src={subtitleIcon} />}
            <span className={styles.info_row_item_subtitle}>{subtitle}</span>
        </div>
    </div>
}

interface ICreatePromocodeCardProps {
    onUpdateInfo: (info: IReferralInfo) => void;
    isMobile: boolean;
}

function CreatePromocodeCard(props: ICreatePromocodeCardProps) {
    const isLoggedIn = useContext(ReferralAppContext)?.isLoggedIn ?? false;
    const [isLoading, setIsLoading] = useState(false);

    async function createCode() {
        if (!isLoggedIn) {
            window.localStorage.setItem("create_code_after_login", "");
            trackGoal("login_or_create_code_or_copy");
            openPassport();
            return;
        }

        if (isLoading) {
            return;
        }

        trackGoal("login_or_create_code_or_copy");
        
        setIsLoading(true);
        try {
            const newInfo = await instance.post('/user_app/promo/create_referral_code')
                .then(() => delay(1000))
                .then(() => instance.get('/user_app/promo/referral', { headers: { 'IC_AppBuild': '104335' } }));
            props.onUpdateInfo(newInfo.data);
            trackGoal("referral_cabinet_code_created");
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);

            // TODO: обработать ошибку создания кода
            // @ts-ignore
            const uiMessage = (error.response?.data as IError)?.error_details.ui_message
            // setError(uiMessage ?? "Что-то пошло не так")
        }
    }

    const conditionsLink = "https://yandex.ru/legal/drive_referral/";

    useEffect(() => {
        if (isLoggedIn && window.localStorage.getItem("create_code_after_login") === "") {
            createCode();
        }
        window.localStorage.removeItem("create_code_after_login");
    }, []);

    if (props.isMobile) {
        if (isLoading) {
            return <div className={styles.create_card_mobile} onClick={() => { }}>
                <CircularProgress />
                <span className={styles.create_card_code_label}>
                    Создаём промокод...
                </span>
            </div>
        } else {
            return <div className={styles.create_card_mobile} onClick={() => createCode()}>
                <img src={referralCreateCode} className={styles.create_card_image_mobile} />
                <span className={styles.create_card_title_mobile}>
                    <u>{isLoggedIn ? "Создать" : "Войти и создать"}</u> промокод на Драйв
                </span>
                <span className={styles.create_card_subtitle_mobile}>
                    Делитесь им с друзьями, приятелями, соседями, родственниками,<br />просто прохожими
                </span>
                <span className={styles.create_card_conditions}>
                    Не можем не добавить, что если создаёте, значит согласны с условиями
                    <br />
                    <a className={styles.create_card_conditions_link} href={conditionsLink} onClick={(e) => {
                        e.stopPropagation();
                    }}>
                        «Реферальной программы Яндекс Драйва»
                    </a>
                </span>
            </div>
        }
    } else {
        if (isLoading) {
            return <div className={styles.create_card}>
                <CircularProgress />
                <span className={styles.create_card_code_label}>
                    Создаём промокод...
                </span>
            </div>
        } else {
            return <div className={styles.create_card} onClick={() => createCode()}>
                <img src={referralCreateCode} className={styles.create_card_image} />
                <span className={styles.create_card_title}>
                    <u>{isLoggedIn ? "Создать" : "Войти и создать"}</u> промокод на Драйв
                </span>
                <span className={styles.create_card_subtitle}>
                    Делитесь им с друзьями, приятелями, соседями, родственниками,<br />просто прохожими
                </span>
                <span className={styles.create_card_conditions}>
                    Не можем не добавить, что если создаёте, значит согласны с условиями
                    <br />
                    <a className={styles.create_card_conditions_link} href={conditionsLink} onClick={(e) => {
                        e.stopPropagation();
                    }}>
                        «Реферальной программы Яндекс Драйва»
                    </a>
                </span>
            </div>
        }
    }
}


function fallbackCopyTextToClipboard(text: string) {
    var textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try { document.execCommand('copy'); } catch (err) { }

    document.body.removeChild(textArea);
}

async function copyTextToClipboard(text: string) {
    try {
        if (!navigator.clipboard) {
            throw "clipboard not supported";
        }
        await navigator.clipboard.writeText(text);
    } catch (error) {
        fallbackCopyTextToClipboard(text);
    }
}

function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}